import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { entregarArchivo } from "../../../../utils/entregarArchivo";
import MultiSelect from "../../../../components/kits/multiSelect";
import { FIELD_FONT, localIsoDate, requestErrorText } from "../../../../components/kits/kitUi";

import { RootState } from "../../../../globals";
import { getAccessPermissions } from "../../../../common";
import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO, KitEnrollmentContext } from "../../../../models/ArticleDTO";
import { kitDeliveryStatus } from "../../../../constants";

/*
	Shared pieces of Tienda → Control de Entregas / Control de Ventas (kits overhaul, Sep 2026).
	No <label> elements anywhere: the app's global CSS positions labels absolutely.
*/

export type Section = "kits" | "kitSales" | "products" | "warehouses" | "purchaseOrders" | "purchaseApprovals" | "purchaseReceipts" | "productMargins" | "transfers" | "stockCounts" | "newSale" | "salesGoals" | "teacherBoard";

export interface ScheduleOption { id: number; code: string; name: string | null; statusId: number; courseId: number; course: string; courseBrief?: string | null; module: string | null; levelId: number; level: string; periodId: number; period: string; mentorId: number | null; mentor: string | null; weekdays: number[]; days: string; hours: string | null; label: string }

export interface FilterOptions {
	courses: { id: string; name: string }[];        // id = CSV of course ids sharing the module name
	periods: { id: number; name: string; start: string | null }[];
	mentors: { id: number; name: string }[];
	schedules: ScheduleOption[];
	advisors: { id: string; name: string }[];
	articles: { id: number; name: string; sku: string; isKit: boolean }[];
	weekdays: { id: number; name: string }[];
}

export interface KitFilters {
	q?: string;
	courseIds?: string[];
	periodIds?: string[];
	scheduleIds?: string[];
	mentorIds?: string[];
	weekdays?: string[];
	articleIds?: string[];
	deliveryStatusIds?: string[];
	statusIds?: string[];
	advisorIds?: string[];
	paidFrom?: string;
	paidTo?: string;
	lastPaidFrom?: string;
	lastPaidTo?: string;
	from?: string;
	to?: string;
	enrolledFrom?: string;
	enrolledTo?: string;
	onlyEnrolled?: string;
	kitStatus?: string;
	isKit?: string;
	productsOnly?: string;
	sort?: string;
	dir?: "asc" | "desc";
}

export const PER_PAGE_DEFAULT = 250;
export const PER_PAGE_OPTIONS = [100, 250, 500];

/** Local calendar date (not UTC: after 6 pm in México/Honduras toISOString is already tomorrow). */
export const isoDate = (date: Date): string => localIsoDate(date);
export const monthOf = (date: Date = new Date()): string => isoDate(date).slice(0, 7);
export const daysAgo = (days: number): string => { const d = new Date(); d.setDate(d.getDate() - days); return isoDate(d); };
export const monthStart = (): string => { const d = new Date(); d.setDate(1); return isoDate(d); };
export const today = (): string => isoDate(new Date());

/** Delivery status labels in Esteban's wording and order. */
export const DELIVERY_OPTIONS: { id: string; name: string }[] = [
	{ id: String(kitDeliveryStatus.PENDING), name: "Sin Entregar" },
	{ id: String(kitDeliveryStatus.PARTIAL), name: "Entrega Parcial" },
	{ id: String(kitDeliveryStatus.UNKNOWN), name: "Sin Registro" },
	{ id: String(kitDeliveryStatus.DELIVERED), name: "Entregado Completo" }
];
export const deliveryLabel = (statusId: number): string => statusId === kitDeliveryStatus.DELIVERED ? "Entregado Completo"
	: statusId === kitDeliveryStatus.PARTIAL ? "Entrega Parcial" : statusId === kitDeliveryStatus.PENDING ? "Sin Entregar" : "-";
export const deliveryColor = (statusId: number): string => statusId === kitDeliveryStatus.DELIVERED ? "var(--wa-verde)"
	: statusId === kitDeliveryStatus.PARTIAL ? "var(--wa-oro)" : statusId === kitDeliveryStatus.PENDING ? "var(--wa-oro)" : "var(--wa-tenue)";

export const PAYMENT_STATUS_OPTIONS: { id: string; name: string }[] = [
	{ id: "2", name: "Pagado Completo" },
	{ id: "1", name: "Con Abono" },
	{ id: "4", name: "Anulado" }
];

export const useCampus = () => {
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const campus: any = sessionCampus || (campusRef && campusRef[currentUserCampus]) || null;
	return { campus, campusId: campus ? Number(campus.id) : 0, currency: campus?.company?.currency?.symbol || campus?.currency || "" };
};

export const usePerms = (section: Section) => {
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	return getAccessPermissions(currentUser, section);
};

export const useIdleLocked = (): boolean => useSelector((state: RootState) => state.idleLockSwitch.value);

export const useFilterOptions = (campusId: number, section: "kits" | "kitSales"): FilterOptions | null => {
	const [options, setOptions] = useState<FilterOptions | null>(null);
	useEffect(() => {
		if (!campusId) return;
		let alive = true;
		HttpManager.getInstance().kitsService.filterOptions(campusId, section)
			.then((response) => { if (alive) setOptions(response.data); })
			.catch(() => { if (alive) setOptions({ courses: [], periods: [], mentors: [], schedules: [], advisors: [], articles: [], weekdays: [] }); });
		return () => { alive = false; };
	}, [campusId, section]);
	return options;
};

/** Debounced copy of a value (text search auto-applies without an "Aplicar" button). */
export const useDebounced = <T,>(value: T, delay = 450): T => {
	const [debounced, setDebounced] = useState<T>(value);
	useEffect(() => { const handle = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(handle); }, [value, delay]);
	return debounced;
};

export const saveExcel = async (campusId: number, section: "kits" | "kitSales", kind: string, tab: string, filters: KitFilters, fileName: string) => {
	const response = await HttpManager.getInstance().kitsService.report(campusId, section, kind, tab, filters as Record<string, any>);
	await entregarArchivo(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName + ".xlsx", "Kits");
};

export const money = (currency: string, value: number): string => currency + value.toFixed(2);

/** Descarga un Excel que el backend arma (respuesta blob). */
export const saveBlob = (data: Blob, fileName: string, origen = "Kits") =>
	entregarArchivo(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName, origen);

/** Enseña en el visor de WAPP (sin otra ventana) un archivo protegido (foto, PDF) que se bajó como blob. */
export const openBlob = (data: Blob, nombre: string = "Archivo") => {
	abrirArchivo({ origen: "Kits", nombre, cargar: () => Promise.resolve(data) });
};

export const apiError = (requestError: any, fallback: string): string => requestErrorText(requestError, fallback);

export const cancelReasonLabel = (reason: string | null | undefined): string => ({
	payment_cancelled: "Pago anulado", invoice_cancelled: "Factura anulada", unpaid_end_of_day: "Sin pago al cierre del día", manual: "Anulación manual"
} as Record<string, string>)[reason || ""] || (reason || "");

/** "María Fernanda López" → "María L." */
export const shortName = (name: string | null | undefined): string => {
	const parts = (name || "").trim().split(/\s+/).filter(Boolean);
	if (parts.length < 1) return "";
	return parts[0] + (parts.length > 1 ? " " + parts[1].charAt(0) + "." : "");
};

// ------------------------------------------------------------------ styles

export const ui = {
	page: { padding: "18px 24px", width: "100%" } as React.CSSProperties,
	title: { margin: 0, fontSize: "26px", fontWeight: 700 } as React.CSSProperties,
	tabs: { display: "flex", gap: "6px", flexWrap: "wrap", margin: "14px 0 10px", alignItems: "center" } as React.CSSProperties,
	badge: (active: boolean): React.CSSProperties => ({ marginLeft: "6px", background: active ? "rgba(255,255,255,0.3)" : "var(--wa-filo)", color: active ? "var(--wa-superficie)" : "var(--wa-tinta)", borderRadius: "10px", padding: "0 8px", fontSize: "12px" }),
	card: { background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderRadius: "10px" } as React.CSSProperties,
	filters: { display: "flex", flexWrap: "wrap", gap: "8px 10px", alignItems: "flex-end", padding: "10px 12px" } as React.CSSProperties,
	field: (width: string): React.CSSProperties => ({ display: "flex", flexDirection: "column", gap: "2px", flex: "0 0 auto", width }),
	caption: { fontSize: "11px", color: "var(--wa-apagado)", lineHeight: 1 } as React.CSSProperties,
	// Campos con fondo y borde visibles (Esteban: los transparentes se ven raros y no se entiende dónde escribir).
	// 16 px on touch screens (iOS zooms the page on smaller fields), 13 px on computers.
	input: { height: "34px", fontSize: FIELD_FONT, backgroundColor: "var(--wa-superficie)", border: "1px solid var(--wa-filo-2)" } as React.CSSProperties,
	small: { fontSize: "11px", color: "var(--wa-apagado)", lineHeight: 1.3 } as React.CSSProperties,
	tiny: { fontSize: "10px", color: "var(--wa-apagado)", lineHeight: 1.2 } as React.CSSProperties,
	muted: { color: "var(--wa-tenue)" } as React.CSSProperties,
	th: { fontSize: "12px", color: "var(--wa-apagado)", textAlign: "left", padding: "8px 10px", borderBottom: "2px solid var(--wa-filo)", whiteSpace: "nowrap", background: "var(--wa-papel-2)" } as React.CSSProperties,
	td: { padding: "7px 10px", borderBottom: "1px solid var(--wa-filo)", verticalAlign: "top", fontSize: "13px" } as React.CSSProperties,
	right: { textAlign: "right" } as React.CSSProperties,
	center: { textAlign: "center" } as React.CSSProperties,
	kpi: { flex: "1 1 150px", padding: "10px 14px", background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderRadius: "10px" } as React.CSSProperties,
	btn: "btn btn-outline-secondary btn-sm rounded-pill",
	btnPrimary: "btn btn-primary btn-sm rounded-pill"
};

// ------------------------------------------------------------------ filter bar (auto-applies; options cascade)

export interface FilterBarShow {
	search?: boolean;
	course?: boolean;
	period?: boolean;
	schedule?: boolean;
	mentor?: boolean;
	weekday?: boolean;
	article?: boolean;
	advisor?: boolean;
	delivery?: boolean;
	paymentStatus?: boolean;
	firstPaidRange?: boolean;
	chargedRange?: boolean;
	enrolledRange?: boolean;
	onlyEnrolled?: boolean;
	kitStatus?: boolean;
}

export interface FilterBarProps {
	options: FilterOptions | null;
	filters: KitFilters;
	show: FilterBarShow;
	busy?: boolean;
	perPage?: number;
	onChange: (patch: Partial<KitFilters>) => void;
	onReset: () => void;
	/** Filters the screen starts with: "Limpiar filtros" only shows when something differs from them. */
	defaults?: KitFilters;
	onPerPage?: (size: number) => void;
	onExport?: () => void;
	exporting?: boolean;
	exportDisabled?: boolean;
}

const courseIdsOf = (option: { id: string }): number[] => option.id.split(",").map(Number);

export const FilterBar: React.FC<FilterBarProps> = ({ options, filters, show, busy, perPage, onChange, onReset, defaults, onPerPage, onExport, exporting, exportDisabled }) => {
	const schedules = options?.schedules || [];
	const sel = {
		courses: (filters.courseIds || []).flatMap((id) => id.split(",").map(Number)),
		periods: (filters.periodIds || []).map(Number),
		schedules: (filters.scheduleIds || []).map(Number),
		mentors: (filters.mentorIds || []).map(Number),
		weekdays: (filters.weekdays || []).map(Number)
	};
	// A schedule matches the current selection except on the dimension being listed (so each list only shows what is still possible).
	const matches = (schedule: ScheduleOption, skip: keyof typeof sel): boolean =>
		(skip === "courses" || !sel.courses.length || sel.courses.includes(schedule.courseId))
		&& (skip === "periods" || !sel.periods.length || sel.periods.includes(schedule.periodId))
		&& (skip === "schedules" || !sel.schedules.length || sel.schedules.includes(schedule.id))
		&& (skip === "mentors" || !sel.mentors.length || (schedule.mentorId !== null && sel.mentors.includes(schedule.mentorId)))
		&& (skip === "weekdays" || !sel.weekdays.length || schedule.weekdays.some((day) => sel.weekdays.includes(day)));

	const cascaded = useMemo(() => {
		const courseIds = new Set<number>(); const periodIds = new Set<number>(); const mentorIds = new Set<number>(); const weekdayIds = new Set<number>(); const scheduleIds = new Set<number>();
		schedules.forEach((schedule) => {
			if (matches(schedule, "courses")) courseIds.add(schedule.courseId);
			if (matches(schedule, "periods")) periodIds.add(schedule.periodId);
			if (matches(schedule, "mentors") && schedule.mentorId !== null) mentorIds.add(schedule.mentorId);
			if (matches(schedule, "weekdays")) schedule.weekdays.forEach((day) => weekdayIds.add(day));
			if (matches(schedule, "schedules")) scheduleIds.add(schedule.id);
		});
		const anySelection = sel.courses.length || sel.periods.length || sel.schedules.length || sel.mentors.length || sel.weekdays.length;
		return {
			courses: (options?.courses || []).filter((course) => !anySelection || courseIdsOf(course).some((id) => courseIds.has(id))),
			periods: (options?.periods || []).filter((period) => !anySelection || periodIds.has(period.id)).map((period) => ({ id: String(period.id), name: period.name })),
			mentors: (options?.mentors || []).filter((mentor) => !anySelection || mentorIds.has(mentor.id)).map((mentor) => ({ id: String(mentor.id), name: mentor.name })),
			weekdays: (options?.weekdays || []).filter((day) => !anySelection || weekdayIds.has(day.id)).map((day) => ({ id: String(day.id), name: day.name })),
			schedules: schedules.filter((schedule) => !anySelection || scheduleIds.has(schedule.id)).map((schedule) => ({ id: String(schedule.id), name: schedule.label }))
		};
	}, [options, filters.courseIds, filters.periodIds, filters.scheduleIds, filters.mentorIds, filters.weekdays]);

	const date = (key: keyof KitFilters, caption: string) => <div style={ui.field("150px")}>
		<span style={ui.caption}>{caption}</span>
		<input type="date" className="form-control" style={ui.input} value={(filters[key] as string) || ""} disabled={busy} onChange={(event) => onChange({ [key]: event.target.value } as Partial<KitFilters>)} />
	</div>;

	// Compared against the screen's default filters (default dates are not "a filter the person put").
	const normalized = (value: any): string => Array.isArray(value) ? [...value].sort().join(",") : (value === undefined || value === null ? "" : String(value));
	const hasAny = Array.from(new Set([...Object.keys(filters), ...Object.keys(defaults || {})])).some((key) => !["sort", "dir", "productsOnly", "onlyEnrolled"].includes(key)
		&& normalized((filters as any)[key]) !== normalized((defaults as any || {})[key]));

	return <div style={{ ...ui.card, ...ui.filters }}>
		{show.search ? <div style={ui.field("240px")}>
			<span style={ui.caption}>Buscar</span>
			<input type="text" className="form-control" style={ui.input} placeholder="Nombre, apellido, teléfono, correlativo, producto" value={filters.q || ""} disabled={busy} onChange={(event) => onChange({ q: event.target.value })} />
		</div> : null}
		{show.course ? <MultiSelect caption="Curso" width="200px" options={cascaded.courses} value={filters.courseIds || []} disabled={busy} onChange={(ids) => onChange({ courseIds: ids })} /> : null}
		{show.period ? <MultiSelect caption="Periodo" width="170px" options={cascaded.periods} value={filters.periodIds || []} disabled={busy} onChange={(ids) => onChange({ periodIds: ids })} /> : null}
		{show.schedule ? <MultiSelect caption="Horario" width="280px" options={cascaded.schedules} value={filters.scheduleIds || []} disabled={busy} onChange={(ids) => onChange({ scheduleIds: ids })} /> : null}
		{show.mentor ? <MultiSelect caption="Profesor" width="190px" options={cascaded.mentors} value={filters.mentorIds || []} disabled={busy} onChange={(ids) => onChange({ mentorIds: ids })} /> : null}
		{show.weekday ? <MultiSelect caption="Día de clase" width="150px" options={cascaded.weekdays} value={filters.weekdays || []} disabled={busy} onChange={(ids) => onChange({ weekdays: ids })} /> : null}
		{show.article ? <MultiSelect caption="Producto" width="220px" options={(options?.articles || []).map((article) => ({ id: String(article.id), name: article.name }))} value={filters.articleIds || []} disabled={busy} onChange={(ids) => onChange({ articleIds: ids })} /> : null}
		{show.advisor ? <MultiSelect caption="Asesora" width="180px" options={options?.advisors || []} value={filters.advisorIds || []} disabled={busy} onChange={(ids) => onChange({ advisorIds: ids })} /> : null}
		{show.delivery ? <MultiSelect caption="Status" width="190px" options={DELIVERY_OPTIONS} value={filters.deliveryStatusIds || []} disabled={busy} onChange={(ids) => onChange({ deliveryStatusIds: ids })} /> : null}
		{show.paymentStatus ? <MultiSelect caption="Status pago" width="170px" options={PAYMENT_STATUS_OPTIONS} value={filters.statusIds || []} disabled={busy} onChange={(ids) => onChange({ statusIds: ids })} /> : null}
		{show.kitStatus ? <div style={ui.field("160px")}>
			<span style={ui.caption}>Kit</span>
			<select className="form-select" style={ui.input} value={filters.kitStatus || ""} disabled={busy} onChange={(event) => onChange({ kitStatus: event.target.value })}>
				<option value="">Todos</option>
				<option value="with">Con kit comprado</option>
				<option value="without">Sin kit</option>
			</select>
		</div> : null}
		{show.firstPaidRange ? <>{date("paidFrom", "Primer Pago Desde")}{date("paidTo", "Primer Pago Hasta")}</> : null}
		{show.chargedRange ? <>{date("from", "Cargado Desde")}{date("to", "Cargado Hasta")}</> : null}
		{show.enrolledRange ? <>{date("enrolledFrom", "Inscrito Desde")}{date("enrolledTo", "Inscrito Hasta")}</> : null}
		{show.onlyEnrolled ? <div style={ui.field("170px")}>
			<span style={ui.caption}>Alumnos</span>
			<select className="form-select" style={ui.input} value={filters.onlyEnrolled === "0" ? "0" : "1"} disabled={busy} onChange={(event) => onChange({ onlyEnrolled: event.target.value })}>
				<option value="1">Con horario activo</option>
				<option value="0">Todos los activos</option>
			</select>
		</div> : null}
		{hasAny ? <button type="button" className={ui.btn} style={{ height: "34px" }} disabled={busy} onClick={onReset}>Limpiar filtros</button> : null}
		<div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "flex-end" }}>
			{onPerPage ? <div style={ui.field("auto")}>
				<span style={ui.caption}>Por página</span>
				<select className="form-select" style={ui.input} value={perPage || PER_PAGE_DEFAULT} disabled={busy} onChange={(event) => onPerPage(Number(event.target.value))}>
					{PER_PAGE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
				</select>
			</div> : null}
			{onExport ? <button type="button" className="btn btn-outline-success rounded-pill" style={{ height: "34px", padding: "0 14px" }} disabled={busy || exporting || exportDisabled} onClick={onExport}>{exporting ? "Generando…" : "Exportar Excel"}</button> : null}
		</div>
	</div>;
};

// ------------------------------------------------------------------ sortable header + pager

export const SortTh: React.FC<{ label: string; sortKey: string; sort?: string; dir?: string; align?: "left" | "right" | "center"; onSort: (key: string) => void }> = ({ label, sortKey, sort, dir, align, onSort }) => {
	const active = sort === sortKey;
	return <th style={{ ...ui.th, textAlign: align || "left", cursor: "pointer", userSelect: "none", color: active ? "var(--wa-acento-hover)" : ui.th.color }} onClick={() => onSort(sortKey)} title="Ordenar">
		{label} <span style={{ fontSize: "10px", opacity: active ? 1 : 0.35 }}>{active ? (dir === "asc" ? "▲" : "▼") : "▼"}</span>
	</th>;
};

export const Pager: React.FC<{ page: number; lastPage: number; total: number; unit: string; busy?: boolean; onPage: (page: number) => void }> = ({ page, lastPage, total, unit, busy, onPage }) => {
	const items: number[] = [];
	for (let index = Math.max(1, page - 3); index <= Math.min(lastPage, page + 3); index++) items.push(index);
	return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0", flexWrap: "wrap", gap: "8px" }}>
		<span style={{ color: "var(--wa-apagado)", fontSize: "13px" }}>{total} {unit}{lastPage > 1 ? ` · página ${page} de ${lastPage}` : ""}</span>
		{lastPage > 1 ? <div style={{ display: "flex", gap: "4px" }}>
			<button type="button" className="btn btn-default btn-sm" disabled={page <= 1 || busy} onClick={() => onPage(1)}>«</button>
			<button type="button" className="btn btn-default btn-sm" disabled={page <= 1 || busy} onClick={() => onPage(page - 1)}>‹</button>
			{items.map((index) => <button key={index} type="button" className={"btn btn-sm " + (index === page ? "btn-primary" : "btn-default")} disabled={busy} onClick={() => onPage(index)}>{index}</button>)}
			<button type="button" className="btn btn-default btn-sm" disabled={page >= lastPage || busy} onClick={() => onPage(page + 1)}>›</button>
			<button type="button" className="btn btn-default btn-sm" disabled={page >= lastPage || busy} onClick={() => onPage(lastPage)}>»</button>
		</div> : null}
	</div>;
};

// ------------------------------------------------------------------ cells

export const StudentCell: React.FC<{ kit: ArticleDTO }> = ({ kit }) => <>
	<div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{kit.studentName || "—"}</div>
	<div style={ui.small}>{kit.studentCode}{kit.studentPhone ? " · " + kit.studentPhone : ""}</div>
</>;

export const ProductCell: React.FC<{ kit: ArticleDTO }> = ({ kit }) => <>
	<div style={{ fontWeight: 600 }}>{kit.name || kit.title}{kit.quantity > 1 ? <span style={{ color: "var(--wa-apagado)", fontWeight: 400 }}> × {kit.quantity}</span> : null}</div>
	<div style={ui.tiny}>SKU {kit.sku}</div>
</>;

/** Curso (nombre breve) · periodo · días y hora · profesor. */
export const EnrollmentCell: React.FC<{ enrollment: KitEnrollmentContext | null | undefined; extra?: number }> = ({ enrollment, extra }) => enrollment
	? <>
		<div style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{enrollment.courseBrief || enrollment.module || enrollment.level}<span style={{ color: "var(--wa-apagado)", fontWeight: 400 }}>{enrollment.period ? " · " + enrollment.period : ""}</span></div>
		<div style={ui.small}>{enrollment.schedule || enrollment.scheduleName || enrollment.scheduleCode}</div>
		<div style={ui.small}>{enrollment.mentor || "Sin profesor"}{extra && extra > 0 ? ` · +${extra} horario${extra > 1 ? "s" : ""}` : ""}</div>
	</>
	: <span style={ui.muted}>Sin horario activo</span>;

export const enrollmentText = (enrollment: KitEnrollmentContext | null | undefined): string => enrollment
	? [enrollment.courseBrief || enrollment.module || enrollment.level, enrollment.period, enrollment.schedule, enrollment.mentor].filter(Boolean).join(" · ") : "";

/** Último pago (grande) y primer pago (chico) cuando hubo más de un pago. */
export const PaidDatesCell: React.FC<{ kit: ArticleDTO }> = ({ kit }) => <div style={{ whiteSpace: "nowrap" }}>
	<div>{kit.paidAt || <span style={ui.muted}>—</span>}</div>
	{kit.paymentsCount && kit.paymentsCount > 1 && kit.firstPaidAt ? <div style={ui.small}>1er pago {kit.firstPaidAt} · {kit.paymentsCount} pagos</div> : null}
</div>;

export const PaymentCell: React.FC<{ kit: ArticleDTO; paidOnly?: boolean }> = ({ kit, paidOnly }) => <div style={{ whiteSpace: "nowrap", textAlign: "right" }}>
	{paidOnly
		? <div>{kit.paidAmount.moneyFormat}</div>
		: <div>{kit.paidAmount.moneyFormat} <span style={{ color: "var(--wa-apagado)" }}>/ {kit.subTotal.moneyFormat}</span></div>}
	{kit.isDebt ? <div style={{ ...ui.small, color: "var(--wa-oro)", fontWeight: 600 }}>Debe {kit.pendingAmount.moneyFormat}</div>
		: kit.isCancelled ? <div style={{ ...ui.small, color: "var(--wa-oro)" }}>Anulado · {cancelReasonLabel(kit.cancelReason)}</div>
		: null}
</div>;

export const StatusCell: React.FC<{ kit: ArticleDTO }> = ({ kit }) => kit.isCancelled || kit.deliveryStatusId === kitDeliveryStatus.UNKNOWN
	? <span style={ui.muted}>-</span>
	: <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
		<div style={{ color: deliveryColor(kit.deliveryStatusId), fontWeight: 600 }}>{deliveryLabel(kit.deliveryStatusId)}</div>
		{kit.deliveredAt ? <div style={ui.small}>{kit.deliveredAt}</div> : null}
	</div>;

export const AdvisorsCell: React.FC<{ kit: ArticleDTO }> = ({ kit }) => (kit.advisors || []).length < 1
	? <span style={ui.muted}>—</span>
	: <span style={{ whiteSpace: "nowrap" }}>{kit.advisors.map((advisor) => shortName(advisor.name)).join(", ")}</span>;

export const ManagerCell: React.FC<{ manager: any }> = ({ manager }) => manager && manager.name
	? <div style={{ whiteSpace: "nowrap" }}>{shortName(manager.name)}<div style={ui.small}>{manager.source === "crm_owner" ? "dueño en CRM" : "nota de kits" + (manager.notedAt ? " · " + manager.notedAt : "")}</div></div>
	: <span style={ui.muted}>—</span>;

export const bestEnrollment = (kit: ArticleDTO): KitEnrollmentContext | null => (kit.enrollments || []).find((e) => e.matchesKit) || (kit.enrollments || [])[0] || null;

export const Loading: React.FC<{ text?: string }> = ({ text }) => <div style={{ padding: "30px", textAlign: "center", color: "var(--wa-apagado)" }}>{text || "Cargando…"}</div>;
export const Empty: React.FC<{ text?: string }> = ({ text }) => <div style={{ padding: "30px", textAlign: "center", color: "var(--wa-apagado)" }}>{text || "Sin registros con estos filtros."}</div>;
